import { useState, useRef, useCallback, useEffect } from 'react'
import { PanelPayload, PanelTab, SseEvent, BlockAction, TraceGroup, AgentStatusEvent } from '../types/panel'
import Stage from './Stage'
import StageErrorBoundary from './StageErrorBoundary'
import { Attachment } from './ChatInput'
import { BelegKarte, GithubEntwurf } from '../types'

const API_URL = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:8000'
const MAX_TABS = 5

// Kopfgruppe des Traces: Events ohne agent-Feld (alte Backends, Sofort-Feedback
// des Endpoints) laufen hier hinein — Fallback auf das flache Verhalten.
const ORCHESTRATOR = 'Orchestrator'

/** Step an die Gruppe des Absenders anhängen; Gruppe bei Bedarf anlegen. */
function appendStep(groups: TraceGroup[], agent: string, step: string): TraceGroup[] {
  const idx = groups.findIndex(g => g.agent === agent)
  if (idx === -1) {
    const neu: TraceGroup = { agent, status: 'running', steps: [step] }
    // Orchestrator-Kopfgruppe steht immer zuerst
    return agent === ORCHESTRATOR ? [neu, ...groups] : [...groups, neu]
  }
  return groups.map((g, i) => (i === idx ? { ...g, steps: [...g.steps, step] } : g))
}

/** agent_status-Event einarbeiten (start: anlegen/aktualisieren, done: abschließen). */
function applyAgentStatus(groups: TraceGroup[], ev: AgentStatusEvent): TraceGroup[] {
  const idx = groups.findIndex(g => g.agent === ev.agent)
  if (idx === -1) {
    // done ohne vorheriges start: nichts anzulegen
    if (ev.status === 'done') return groups
    const neu: TraceGroup = {
      agent: ev.agent, status: 'running',
      model: ev.model, schwierigkeit: ev.schwierigkeit, steps: [],
    }
    return ev.agent === ORCHESTRATOR ? [neu, ...groups] : [...groups, neu]
  }
  return groups.map((g, i) => (i === idx
    ? {
        ...g,
        status: ev.status === 'done' ? 'done' as const : 'running' as const,
        model: ev.model ?? g.model,
        schwierigkeit: ev.schwierigkeit ?? g.schwierigkeit,
      }
    : g))
}

interface Props {
  authToken: string
  conversationId: number | null
  onConversationId: (id: number) => void
  onAssistantMessage: (text: string, agentUsed?: string, panel?: { ref: string; title: string }) => void
  /** Token-Streaming: Live-Aktualisierung der laufenden Antwort (voller Text des aktuellen Turns) */
  onAssistantDelta?: (fullText: string) => void
  onError: (msg: string) => void
  onLoadingChange: (loading: boolean) => void
  onReady?: (fn: (content: string, attachments?: Attachment[]) => Promise<void>) => void
  onStagedChange?: (staged: boolean) => void
  /** Gruppierter Tool-Trace für die k-trace-Darstellung im Chat (+ Laufzeit in s nach Abschluss) */
  onThinking?: (groups: TraceGroup[], active: boolean, durationSeconds?: number) => void
  /** Live-Reasoning (reasoning_delta): akkumulierter Text des aktuellen Turns, '' = ausblenden */
  onReasoning?: (text: string) => void
  /** Finales Reasoning nach Abschluss des Laufs ('' = keins) — für den eingeklappten Trace */
  onReasoningFinal?: (text: string) => void
  /** Stellt eine Funktion bereit, mit der der Chat einen Bühnen-Tab öffnen kann */
  onOpenStageReady?: (fn: (ref: string) => void) => void
  /** Stellt eine Abbruch-Funktion für den laufenden Stream bereit (Stop-Button) */
  onAbortReady?: (fn: () => void) => void
  /** GitHub-Ticket-Entwurf des Agenten (Bestätigungskarte im Chat) */
  onGithubEntwurf?: (entwurf: GithubEntwurf) => void
  /** Belegkarte des Agenten (Rechnung/Lieferschein/Zahlung) zur Bestätigung im Chat */
  onBelegKarte?: (karte: BelegKarte) => void
  children: React.ReactNode
}

export default function SplitWorkspace({
  authToken, conversationId, onConversationId,
  onAssistantMessage, onAssistantDelta, onError, onLoadingChange, onReady, onStagedChange,
  onThinking, onReasoning, onReasoningFinal, onOpenStageReady, onAbortReady, onGithubEntwurf, onBelegKarte, children
}: Props) {
  // Tabs synchron aus localStorage hydratisieren — ein Hydrations-Effekt
  // kollidiert im StrictMode mit dem Persist-Effekt (schreibt [] zurück)
  const [tabs, setTabs] = useState<PanelTab[]>(() => {
    try {
      const saved = localStorage.getItem('erp-panel-tabs')
      if (!saved) return []
      const parsed: PanelTab[] = JSON.parse(saved)
      return parsed.slice(-MAX_TABS)
    } catch {
      return []
    }
  })
  const [activeTabId, setActiveTabId] = useState<string | null>(
    () => localStorage.getItem('erp-panel-active')
  )
  // Bühnen-Disziplin: Offen-Zustand der Bühne, persistiert in localStorage.
  // Beim Laden werden Tabs zwar wiederhergestellt, die Bühne zeigt sich aber
  // nur, wenn sie zuletzt bewusst offen war — sonst reißt jeder Reload sie auf.
  const [stageOpen, setStageOpen] = useState<boolean>(
    () => localStorage.getItem('erp-stage-open') === '1'
  )
  const [traceGroups, setTraceGroups] = useState<TraceGroup[]>([])
  const [isThinking, setIsThinking] = useState(false)
  // Laufzeit des Laufs (Sekunden) — beim Send-Start gemessen, beim Abschluss
  // gesetzt und für die Einzeiler-Zusammenfassung an den Chat gemeldet
  const [traceDuration, setTraceDuration] = useState(0)
  const sendStartRef = useRef(0)
  const tabCounter = useRef(0)
  const abortRef = useRef<AbortController | null>(null)

  // Panel, das während des aktuellen Streams geöffnet wurde — wird der
  // nächsten Textantwort als Bühnen-Verweis (k-msg__stageref) angeheftet
  const pendingPanel = useRef<{ ref: string; title: string } | null>(null)

  // Token-Streaming: akkumulierter Text des aktuellen Turns. text_reset leert
  // ihn (neuer Turn), text_chunk finalisiert die Antwort.
  const streamBuffer = useRef('')

  // Live-Reasoning: akkumulierter reasoning_delta-Text des aktuellen Turns.
  // Die Live-Zeile verschwindet, sobald die eigentliche Antwort streamt; der
  // Text wandert dabei in finalReasoningRef und bleibt bis zum nächsten Send
  // erhalten (für das "Gedacht" im eingeklappten Trace).
  const reasoningBuffer = useRef('')
  const finalReasoningRef = useRef('')

  // Archiv aller je erzeugten Panels (ref -> payload). Tabs sind auf MAX_TABS
  // begrenzt und werden beim Schließen geleert; das Archiv überlebt das, damit
  // der "Auswertung auf der Bühne"-Button auch ein verdrängtes Panel wieder
  // öffnen kann.
  const panelArchive = useRef<Map<string, PanelPayload>>(new Map())

  // Panel nur archivieren (Bühne bleibt zu): Ref-Id über denselben Zähler wie
  // addTab vergeben, damit es keine Id-Kollisionen mit echten Tabs gibt. Der
  // Eintrag ist später über openStage (Verweis-Chip) als Tab herstellbar.
  const archivePanel = useCallback((payload: PanelPayload): string => {
    tabCounter.current += 1
    const id = `tab-${tabCounter.current}`
    panelArchive.current.set(id, payload)
    return id
  }, [])

  const addTab = useCallback((payload: PanelPayload): string => {
    const id = archivePanel(payload)
    setTabs(prev => {
      const next = [...prev, { id, payload }]
      return next.length > MAX_TABS ? next.slice(next.length - MAX_TABS) : next
    })
    setActiveTabId(id)
    // Auto-Open: ein neues Panel darf eine zuvor geschlossene Bühne wieder öffnen
    setStageOpen(true)
    return id
  }, [archivePanel])

  // Bühnen-Tab (re-)öffnen: fehlt der Tab (verdrängt/geschlossen/nie getabbt),
  // aus dem Archiv wiederherstellen, dann aktivieren.
  const openStage = useCallback((ref: string) => {
    setTabs(prev => {
      if (prev.some(t => t.id === ref)) return prev
      const payload = panelArchive.current.get(ref)
      if (!payload) return prev
      const next = [...prev, { id: ref, payload }]
      return next.length > MAX_TABS ? next.slice(next.length - MAX_TABS) : next
    })
    setActiveTabId(ref)
    // Chip-Klick ist ein bewusstes Öffnen der Bühne
    setStageOpen(true)
  }, [])

  // Lauf abschließen (done/error/abort): Laufzeit festhalten, Live-Reasoning
  // in den "abgeschlossen"-Zustand überführen (erst der nächste Send leert es).
  const finishTrace = useCallback(() => {
    if (sendStartRef.current) {
      setTraceDuration(Math.max(1, Math.round((Date.now() - sendStartRef.current) / 1000)))
    }
    if (reasoningBuffer.current) {
      finalReasoningRef.current = reasoningBuffer.current
      reasoningBuffer.current = ''
    }
    onReasoning?.('')
    onReasoningFinal?.(finalReasoningRef.current)
    setIsThinking(false)
  }, [onReasoning, onReasoningFinal])

  const handleEvent = useCallback((event: SseEvent) => {
    switch (event.type) {
      case 'thinking':
        // Ohne agent-Feld (alte Backends/Endpoint-Feedback): Orchestrator-Gruppe
        setTraceGroups(prev => appendStep(prev, event.agent ?? ORCHESTRATOR, event.step))
        break
      case 'agent_status':
        setTraceGroups(prev => applyAgentStatus(prev, event))
        break
      case 'reasoning_delta':
        reasoningBuffer.current += event.text
        onReasoning?.(reasoningBuffer.current)
        break
      case 'blocks': {
        const payload = { panelTitle: event.panelTitle, panelBadge: event.panelBadge, blocks: event.blocks }
        // Bühnen-Disziplin: autoOpen fehlt bei alten Backends → true (Altverhalten).
        // Nur bei explizitem false bleibt die Bühne zu — das Panel wandert ins
        // Archiv und ist über den Verweis-Chip an der Nachricht erreichbar.
        const id = event.autoOpen === false ? archivePanel(payload) : addTab(payload)
        pendingPanel.current = { ref: id, title: event.panelTitle }
        break
      }
      case 'text_reset':
        streamBuffer.current = ''
        onAssistantDelta?.('')
        // Neuer Turn: Reasoning des vorherigen Turns aufbewahren, Zeile ausblenden
        if (reasoningBuffer.current) {
          finalReasoningRef.current = reasoningBuffer.current
          reasoningBuffer.current = ''
        }
        onReasoning?.('')
        break
      case 'text_delta':
        streamBuffer.current += event.text
        onAssistantDelta?.(streamBuffer.current)
        // Sobald die eigentliche Antwort streamt, verschwindet die Reasoning-Zeile
        // (Text bleibt für das "Gedacht" nach Abschluss erhalten)
        if (event.text && reasoningBuffer.current) {
          finalReasoningRef.current = reasoningBuffer.current
          reasoningBuffer.current = ''
          onReasoning?.('')
        }
        break
      case 'text_chunk':
        // Autoritativer Abschluss: ersetzt die gestreamte Live-Antwort
        onAssistantMessage(event.text, undefined, pendingPanel.current ?? undefined)
        pendingPanel.current = null
        streamBuffer.current = ''
        break
      case 'github_entwurf':
        // Ticket-Entwurf des Agenten: eigene Karte im Chat, Anlegen erst nach Bestätigung
        onGithubEntwurf?.({
          entwurf_id: event.entwurf_id,
          titel: event.titel,
          beschreibung: event.beschreibung,
          typ: event.typ,
          status: 'OFFEN',
        })
        break
      case 'beleg_karte': {
        // Beleg des Agenten (nur Entwurf): Karte im Chat, Aktion nach außen erst per Klick
        const { type: _type, ...karte } = event
        onBelegKarte?.(karte)
        break
      }
      case 'done':
        if (event.conversation_id) onConversationId(event.conversation_id)
        finishTrace()
        break
      case 'error':
        onError(event.message)
        finishTrace()
        break
    }
  }, [addTab, archivePanel, onConversationId, onAssistantMessage, onAssistantDelta, onGithubEntwurf, onBelegKarte, onReasoning, onError, finishTrace])

  // Gruppierten Trace an den Chat melden
  useEffect(() => {
    onThinking?.(traceGroups, isThinking, traceDuration)
  }, [traceGroups, isThinking, traceDuration, onThinking])

  // Bühnen-Tab aus dem Chat heraus öffnen (k-msg__stageref)
  useEffect(() => {
    onOpenStageReady?.(openStage)
  }, [onOpenStageReady, openStage])

  // Laufenden Stream abbrechen (Stop-Button im Composer). Bereits gestreamter
  // Text bleibt als Nachricht stehen; Zustand wird im finally von sendMessage
  // zurückgesetzt.
  const abortStream = useCallback(() => {
    abortRef.current?.abort()
  }, [])

  useEffect(() => {
    onAbortReady?.(abortStream)
  }, [onAbortReady, abortStream])

  const sendMessage = useCallback(async (content: string, attachments: Attachment[] = []) => {
    abortRef.current?.abort()
    const controller = new AbortController()
    abortRef.current = controller

    // Neuer Lauf: alter Trace + aufbewahrtes Reasoning verschwinden jetzt wirklich
    setTraceGroups([])
    setTraceDuration(0)
    sendStartRef.current = Date.now()
    setIsThinking(true)
    onLoadingChange(true)
    streamBuffer.current = ''
    reasoningBuffer.current = ''
    finalReasoningRef.current = ''
    onReasoning?.('')
    onReasoningFinal?.('')

    try {
      const response = await fetch(`${API_URL}/api/chat/stream`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify({
          message: content,
          conversation_id: conversationId,
          attachments: attachments.map(a => ({
            type: a.type,
            name: a.name,
            mime_type: a.mimeType,
            base64: a.base64,
          })),
        }),
        signal: controller.signal
      })

      if (!response.ok || !response.body) {
        // FastAPI liefert Fehler als {"detail": "..."} – den Klartext anzeigen statt nur "HTTP 400".
        let detail = ''
        try {
          const body = await response.json()
          if (typeof body?.detail === 'string') detail = body.detail
        } catch { /* kein JSON-Body */ }
        throw new Error(detail || `HTTP ${response.status}`)
      }

      const reader = response.body.getReader()
      const decoder = new TextDecoder()
      let buffer = ''

      while (true) {
        const { done, value } = await reader.read()
        if (done) break

        buffer += decoder.decode(value, { stream: true })
        const lines = buffer.split('\n')
        buffer = lines.pop() ?? ''

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue
          try {
            const event: SseEvent = JSON.parse(line.slice(6))
            handleEvent(event)
          } catch {
            // skip malformed events
          }
        }
      }
    } catch (err: unknown) {
      if (err instanceof Error && err.name === 'AbortError') {
        // Stop-Button oder Navigation: kein Fehler. Bereits gestreamter Text
        // bleibt als (unfertige) Nachricht im Verlauf stehen.
      } else {
        onError(err instanceof Error ? err.message : 'Verbindungsfehler')
      }
    } finally {
      onLoadingChange(false)
      // Beim Abbruch kommt kein done-Event mehr — Trace/Reasoning sicher abschließen
      finishTrace()
    }
  }, [authToken, conversationId, handleEvent, onLoadingChange, onReasoning, onReasoningFinal, onError, finishTrace])

  useEffect(() => {
    onReady?.(sendMessage)
  }, [onReady, sendMessage])

  // tabCounter an restaurierte Tab-Ids anpassen (keine Id-Kollisionen) und
  // das Archiv aus den aus localStorage geladenen Tabs vorbefüllen
  useEffect(() => {
    tabCounter.current = tabs.reduce((acc, t) => {
      const n = parseInt(t.id.split('-')[1] ?? '0', 10)
      return n > acc ? n : acc
    }, tabCounter.current)
    tabs.forEach(t => panelArchive.current.set(t.id, t.payload))
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // Persist tabs whenever they change
  useEffect(() => {
    localStorage.setItem('erp-panel-tabs', JSON.stringify(tabs))
  }, [tabs])

  // Persist active tab whenever it changes
  useEffect(() => {
    if (activeTabId !== null) {
      localStorage.setItem('erp-panel-active', activeTabId)
    }
  }, [activeTabId])

  // Bühnen-Offen-Zustand persistieren (Reload reißt die Bühne nicht mehr auf)
  useEffect(() => {
    localStorage.setItem('erp-stage-open', stageOpen ? '1' : '0')
  }, [stageOpen])

  useEffect(() => {
    return () => { abortRef.current?.abort() }
  }, [])

  const handleAction = useCallback((action: BlockAction) => {
    if (action.kind === 'toast') {
      alert(action.text)
    }
  }, [])

  // Bühne vollständig zurücksetzen (Tabs + persistierter Zustand). Wird von
  // der ErrorBoundary genutzt, damit eine fehlerhaft persistierte Auswertung
  // nicht bei jedem Laden erneut die App aushängt.
  const resetStage = useCallback(() => {
    setTabs([])
    setActiveTabId(null)
    setStageOpen(false)
    localStorage.removeItem('erp-panel-tabs')
    localStorage.removeItem('erp-panel-active')
  }, [])

  // Bühne sichtbar nur wenn bewusst geöffnet UND Tabs vorhanden — restaurierte
  // Tabs allein (Reload) zeigen die Bühne nicht mehr automatisch
  const stageVisible = stageOpen && tabs.length > 0

  // Bühnen-Choreografie: is-staged am k-app-Root (siehe kontor.css)
  useEffect(() => {
    onStagedChange?.(stageVisible)
  }, [stageVisible, onStagedChange])

  return (
    <div className="k-work">
      <section className="k-chat" data-screen-label="Agent-Chat">
        {children}
      </section>
      <div className="k-stage-col">
        {stageVisible && (
          <StageErrorBoundary key={activeTabId ?? 'none'} onReset={resetStage}>
            <Stage
              tabs={tabs}
              activeTabId={activeTabId}
              onTabSelect={setActiveTabId}
              onClose={resetStage}
              onAction={handleAction}
            />
          </StageErrorBoundary>
        )}
      </div>
    </div>
  )
}
